import { ApiResponse } from "../models/api-response";
import {
  JobOffer,
  Candidate,
  JobOfferSource,
  Skill, JobPipeline, PipelineStage,
  MatchingPriorities,
  MatchingPriorityLevel,
  MATCHING_PRIORITY_LEVELS,
  DEFAULT_MATCHING_PRIORITIES,
} from "../models/job";
import { callApi } from "./api.service";
import type { AcceptCandidatePayload } from "../utils/bookingLink";
import {
  JobStatus,
  ApiJobOffer,
  ApiDocument,
  Document,
  PaginatedJobOffersResponse,
  JobOfferCreateData,
  JobOfferUpdateData,
  JobRescoreSummary,
  DocumentUploadResponse,
  JobStatistics,
  CandidateApiResponse,
  CandidateAssessmentSummary,
  CandidateStatusUpdate,
  ExamConfig,
  JobPlannedQuestionsListResponse,
  CandidateQuestionsAnswersResponse,
  ResumeSearchRequest,
  ResumeSearchResponse,
  ResumeUploadResponse,
  LinkResumeRequest,
  LinkResumeResponse,
  PipelineApiResponse,
  MoveCandidateApiResponse,
  CandidatePositionApiResponse,
} from "../types/job.types";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

/** Platforms the backend can score today. Mirrors `SocialLinkType` (subset). */
export type SocialResourcePlatform = 'leetcode' | 'google_scholar' | 'blog' | 'github';

export interface SocialResourceScoringResult {
  source_url: string;
  username: string;
  /** LeetCode uses numbers; blog scoring adds strings (e.g. persona) and nested metrics. */
  scores: Record<string, number | boolean | string>;
  summary: string;
  analyzed_at: string;
  /** Optional structured side-payloads (currently blog `features` / `identity`). */
  extras?: Record<string, unknown>;
}

export interface SocialResourceScoringTaskResponse {
  candidate_id: string;
  task_id: string;
  status: string;
}

// Convert snake_case API document to camelCase frontend model
function mapApiDocumentToModel(apiDocument: ApiDocument): Document {
  return {
    id: apiDocument.id,
    jobOfferId: apiDocument.job_offer_id,
    documentType: apiDocument.document_type,
    fileName: apiDocument.file_name,
    filePath: apiDocument.file_path,
    fileSize: apiDocument.file_size,
    mimeType: apiDocument.mime_type,
    extractedData: apiDocument.extracted_data,
    processingStatus: apiDocument.processing_status,
    createdAt: apiDocument.created_at,
    updatedAt: apiDocument.updated_at || "",
  };
}

function normalizeSkillArray(input: unknown): Array<string | Skill> {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) => {
      if (typeof item === 'string') {
        const name = item.trim();
        return name ? name : null;
      }
      if (item && typeof item === 'object' && 'name' in item) {
        const rawName = (item as { name?: unknown }).name;
        const rawImportance = (item as { importance?: unknown }).importance;
        if (typeof rawName !== 'string' || !rawName.trim()) return null;
        const importance =
          rawImportance === 'nice_to_have' || rawImportance === 'uncertain' || rawImportance === 'must_have'
            ? rawImportance
            : 'must_have';
        return { name: rawName.trim(), importance };
      }
      return null;
    })
    .filter((item): item is string | Skill => item !== null);
}

function normalizeMatchingPriorities(input: unknown): MatchingPriorities {
  const fallback = DEFAULT_MATCHING_PRIORITIES;
  if (!input || typeof input !== 'object') {
    return fallback;
  }

  const source = input as Record<string, unknown>;
  const toLevel = (value: unknown, fb: MatchingPriorityLevel): MatchingPriorityLevel =>
    MATCHING_PRIORITY_LEVELS.includes(value as MatchingPriorityLevel)
      ? (value as MatchingPriorityLevel)
      : fb;

  return {
    yoe: toLevel(source.yoe, fallback.yoe),
    skills_match: toLevel(source.skills_match, fallback.skills_match),
    degree: toLevel(source.degree, fallback.degree),
    sector_experience: toLevel(source.sector_experience, fallback.sector_experience),
  };
}

/**
 * Converts frontend form data (display field names) to backend format (snake_case field names).
 * Handles data type conversions: tag fields to arrays, number fields to integers.
 */
function convertFormDataToBackendFormat(formData: Record<string, unknown>): Record<string, unknown> {
  const fieldMap: Record<string, string> = {
    'Job Title': 'title',
    'Remote Policy': 'remote_policy',
    'Contract Type': 'contract_type',
    'Location': 'location',
    'Years of Experience': 'years_of_experience',
    'Required Degree': 'required_degree',
    'Role Description': 'mission_role_in_job',
    'Skills': 'skills',
    'Company Name': 'company_name',
    'Company Sector': 'company_sector',
    'Company Description': 'company_description',
    'Education Requirements': 'education_requirements',
    'Business Knowledge': 'business_knowledge',
    'Soft Skills': 'soft_skills',
    'Matching Priorities': 'matching_priorities',
  };

  const tagFields = new Set(['Skills', 'Business Knowledge', 'Soft Skills']);
  const numberFields = new Set(['Years of Experience', 'Company Size']);

  const backendData: Record<string, unknown> = {};
  
  for (const [displayName, value] of Object.entries(formData)) {
    const isTagField = tagFields.has(displayName);
    if (!isTagField && (value === null || value === undefined)) {
      continue;
    }
    
    const backendField = fieldMap[displayName];
    if (!backendField) continue;

    if (isTagField) {
      const items = normalizeSkillArray(value);
      if (items.length) backendData[backendField] = items;
    } else if (displayName === 'Matching Priorities' && value && typeof value === 'object') {
      backendData[backendField] = normalizeMatchingPriorities(value);
    } else if (numberFields.has(displayName)) {
      const num = parseInt(String(value), 10);
      if (!isNaN(num)) backendData[backendField] = num;
    } else {
      const textValue = String(value).trim();
      if (!textValue) continue;
      backendData[backendField] = textValue;
    }
  }

  return backendData;
}

// Convert snake_case API job offer to camelCase frontend model
function mapApiJobOfferToModel(apiJob: ApiJobOffer): JobOffer {
  const mappedDocuments = apiJob.documents 
    ? apiJob.documents.map(mapApiDocumentToModel) 
    : [];
  const unifiedSkills = normalizeSkillArray(apiJob.skills);

  return {
    id: apiJob.id,
    title: apiJob.title,
    description: apiJob.description,
    educationRequirements: apiJob.education_requirements,
    skills: unifiedSkills,
    businessKnowledge: apiJob.business_knowledge,
    softSkills: apiJob.soft_skills,
    yearsOfExperience: apiJob.years_of_experience,
    customInstructions: apiJob.custom_instructions || "",
    isActive: apiJob.is_active,
    status: apiJob.status,  // Already using the correct lowercase values
    createdById: apiJob.created_by_id,
    creator_email: apiJob.creator_email,
    createdAt: apiJob.created_at,
    updatedAt: apiJob.updated_at || "",
    documents: mappedDocuments,
    remotePolicy: apiJob.remote_policy,
    contractType: apiJob.contract_type,
    location: apiJob.location,
    requiredDegree: apiJob.required_degree,
    companyName: apiJob.company_name,
    companySector: apiJob.company_sector,
    companySize: apiJob.company_size,
    companyDescription: apiJob.company_description,
    mission_role_in_job: apiJob.mission_role_in_job,
    authorizeCvUpload: apiJob.authorize_cv_upload,
    enableCvAutoInterview: apiJob.enable_cv_auto_interview,
    cvInterviewThreshold: apiJob.cv_interview_threshold,
    brief_exam: apiJob.brief_exam,
    candidateCount: apiJob.candidate_count ?? 0,
    requiresReview: apiJob.requires_review ?? false,
    source: (apiJob.source as JobOfferSource) || 'MANUAL',
    matchingPriorities: normalizeMatchingPriorities(apiJob.matching_priorities),
    locale: apiJob.locale,
    workspaceLogoUrl: apiJob.workspace_logo_url,
    workspacePrimaryColor: apiJob.workspace_primary_color ?? null,
    workspaceSecondaryColor: apiJob.workspace_secondary_color ?? null,
  };
}

// Get all job offers with pagination
export const getJobOffers = async (
  accessToken: string,
  isActive?: boolean,
  status?: JobStatus,
  page: number = 1,
  size: number = 20,
  searchTerm?: string,
  searchScope: 'user' | 'admin' = 'user'
): Promise<ApiResponse<PaginatedJobOffersResponse>> => {
  // Build query string
  const queryString = new URLSearchParams();
  
  if (isActive !== undefined) {
    queryString.append('is_active', String(isActive));
  }
  
  if (status !== undefined) {
    queryString.append('status', status);
  }
  
  queryString.append('page', String(page));
  queryString.append('size', String(size));

  if (searchTerm && searchTerm.trim()) {
    queryString.append('search', searchTerm.trim());
  }

  if (searchScope) {
    queryString.append('search_scope', searchScope);
  }
  
  const queryPart = `?${queryString.toString()}`;
  
  interface ApiPaginatedJobOffersResponse {
    items: ApiJobOffer[];
    total: number;
    page: number;
    pages: number;
    size: number;
  }

  const response = await callApi<ApiPaginatedJobOffersResponse>(`${apiServerUrl}/api/jobs/offers${queryPart}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    }
  });
  
  if (response.data) {
    // Map the API response to our frontend model
    return {
      data: {
        items: response.data.items.map(mapApiJobOfferToModel),
        total: response.data.total,
        page: response.data.page,
        pages: response.data.pages,
        size: response.data.size
      },
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Get a specific job offer by ID
export const getJobOfferById = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    }
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Create a new job offer
export const createJobOffer = async (
  accessToken: string,
  jobData: JobOfferCreateData
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(jobData),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Update a job offer. Scoring-relevant edits rescore candidates in the background.
export const updateJobOffer = async (
  accessToken: string,
  jobId: string,
  jobData: JobOfferUpdateData
): Promise<ApiResponse<{ job: JobOffer; rescore: JobRescoreSummary }>> => {
  const response = await callApi<ApiJobOffer & { rescore: JobRescoreSummary }>(
    `${apiServerUrl}/api/jobs/offers/${jobId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(jobData),
    }
  );

  if (response.data) {
    return {
      data: { job: mapApiJobOfferToModel(response.data), rescore: response.data.rescore },
      error: response.error
    };
  }

  return {
    data: undefined,
    error: response.error
  };
};

// Dry run of a job edit: does it change candidate scores, and how many candidates
// (= CV credits) would be rescored. Nothing is written.
export const previewJobEditImpact = async (
  accessToken: string,
  jobId: string,
  jobData: JobOfferUpdateData
): Promise<ApiResponse<JobRescoreSummary>> => {
  return callApi<JobRescoreSummary>(`${apiServerUrl}/api/jobs/offers/${jobId}/edit-impact`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(jobData),
  });
};

// Update job status
export const updateJobStatus = async (
  accessToken: string,
  jobId: string,
  status: JobStatus
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ status }),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Toggle authorize CV upload for a job offer
export const toggleAuthorizeCvUpload = async (
  accessToken: string,
  jobId: string,
  authorize: boolean
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/authorize-cv-upload`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ authorize_cv_upload: authorize }),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Update CV interview threshold for a job offer
export const updateCvInterviewThreshold = async (
  accessToken: string,
  jobId: string,
  threshold: number
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/cv-interview-threshold`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ cv_interview_threshold: threshold }),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Toggle enable CV auto-interview for a job offer
export const toggleEnableCvAutoInterview = async (
  accessToken: string,
  jobId: string,
  enable: boolean
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/enable-cv-auto-interview`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ enable_cv_auto_interview: enable }),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Utility function to convert camelCase object keys to snake_case
const toSnakeCase = <T extends Record<string, unknown>>(obj: T): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    result[snakeKey] = obj[key];
  });
  return result;
};

export const selectCandidatesForInterview = async (
  accessToken: string,
  jobId: string,
  candidateIds: string[],
  examConfig: ExamConfig,
  sendNotification = true
): Promise<ApiResponse<JobOffer>> => {
  // Transform camelCase to snake_case for backend compatibility
  const transformedExamConfig = {
    technical: toSnakeCase(examConfig.technical),
    soft_skills: toSnakeCase(examConfig.softSkills)
  };

  const requestBody = {
    candidate_ids: candidateIds,
    exam_config: transformedExamConfig,
    send_notification: sendNotification
  };
  
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/select-candidates`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Archive a job offer (set is_active to false)
export const archiveJobOffer = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<JobOffer>> => {
  const config: RequestInit = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/archive`, config);
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Update custom instructions for a job offer
export const updateCustomInstructions = async (
  accessToken: string,
  jobId: string,
  customInstructions: string
): Promise<ApiResponse<JobOffer>> => {
  // Create FormData to send as multipart/form-data
  const formData = new FormData();
  formData.append('custom_instructions', customInstructions);
  
  const config: RequestInit = {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  };

  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/custom-instructions`, config);
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Upload a document for a job offer
export const uploadDocument = async (
  accessToken: string,
  jobId: string,
  file: File,
): Promise<ApiResponse<DocumentUploadResponse>> => {
  // Create FormData to send as multipart/form-data
  const formData = new FormData();
  formData.append('file', file);
  
  return callApi<DocumentUploadResponse>(`${apiServerUrl}/api/jobs/offers/${jobId}/documents`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
};

// Upload CVs for an active job offer (supports single or multiple files)
export const uploadCvsForJob = async (
  accessToken: string,
  jobId: string,
  files: File[],
): Promise<ApiResponse<DocumentUploadResponse[]>> => {
  // Create FormData to send as multipart/form-data
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  
  return callApi<DocumentUploadResponse[]>(`${apiServerUrl}/api/jobs/offers/${jobId}/add-cvs`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
};

// Upload resumes to resume bank (not linked to any job)
export const uploadResumesToBank = async (
  accessToken: string,
  files: File[],
): Promise<ApiResponse<ResumeUploadResponse[]>> => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  
  const url = `${apiServerUrl}/api/resume-bank/upload`;
  
  const response = await callApi<ResumeUploadResponse[]>(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  
  return response;
};

export const searchResumeBank = async (
  accessToken: string,
  request: ResumeSearchRequest,
): Promise<ApiResponse<ResumeSearchResponse>> => {
  const url = `${apiServerUrl}/api/resume-bank/search`;
  
  const response = await callApi<ResumeSearchResponse>(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  });
  
  return response;
};

export const linkResumesToJob = async (
  accessToken: string,
  request: LinkResumeRequest,
): Promise<ApiResponse<LinkResumeResponse>> => {
  const url = `${apiServerUrl}/api/jobs/offers/${request.job_offer_id}/link-resumes`;
  
  const response = await callApi<LinkResumeResponse>(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  
  return response;
};

// Get all documents for a job offer
export const getDocumentsForJob = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<Document[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await callApi<ApiDocument[]>(`${apiServerUrl}/api/jobs/offers/${jobId}/documents`, config);
  
  if (response.data) {
    return {
      data: response.data.map(mapApiDocumentToModel),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Delete a document
export const deleteDocument = async (
  accessToken: string,
  jobId: string,
  documentId: string
): Promise<ApiResponse<null>> => {
  const config: RequestInit = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<null>(`${apiServerUrl}/api/jobs/offers/${jobId}/documents/${documentId}`, config);
};

// Analyze all documents for a job
export const analyzeDocuments = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<JobOffer>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    }
  };

  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/analyze-documents`, config);
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Fast-path: analyze a typed / dictated job description without uploading a file.
// Backend skips document classification and aggregation — single LLM extraction call.
export const analyzeJobFromText = async (
  accessToken: string,
  jobId: string,
  text: string,
  customInstructions?: string
): Promise<ApiResponse<{ message: string; job_id: string }>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      text,
      custom_instructions: customInstructions ?? undefined,
    }),
  };

  return await callApi<{ message: string; job_id: string }>(
    `${apiServerUrl}/api/jobs/offers/${jobId}/from-text`,
    config
  );
};

// Delete a job offer
export const deleteJobOffer = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<null>> => {
  const config: RequestInit = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<null>(`${apiServerUrl}/api/jobs/offers/${jobId}`, config);
};

// Get job statistics
export const getJobStatistics = async (
  accessToken: string
): Promise<ApiResponse<JobStatistics>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<JobStatistics>(`${apiServerUrl}/api/jobs/statistics`, config);
};

// Get all candidates for a job offer
export const getJobCandidates = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<Candidate[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await callApi<CandidateApiResponse[]>(`${apiServerUrl}/api/jobs/offers/${jobId}/candidates`, config);
  
  if (response.data) {
    const candidates = response.data.map((candidate: CandidateApiResponse) => ({
      id: candidate.id,
      talentId: candidate.talent_id,
      talentPoolProfileId: candidate.talent_pool_profile_id ?? undefined,
      jobOfferId: candidate.job_offer_id,
      source: (candidate.source as Candidate['source']) ?? 'cv_upload',
      score: candidate.score ?? undefined,
      talentName: candidate.talent_name,
      talentEmail: candidate.talent_email,
      talentLinkedinUrl: candidate.talent_linkedin_url ?? undefined,
      enhancedData: candidate.enhanced_data ?? undefined,
      createdAt: candidate.created_at,
      updatedAt: candidate.updated_at || candidate.created_at,
      hasAssessmentAvailable: candidate.has_assessment_available,
      hasValidEmail: candidate.has_valid_email,
      interviewInvitationToken: candidate.interview_invitation_token ?? undefined,
      interviewId: candidate.interview_id ?? undefined,
      interviewStatus: candidate.interview_status ?? undefined,
      interviewExpiresAt: candidate.interview_expires_at ?? undefined,
      pipelineStageId: candidate.pipeline_stage_id ?? undefined,
      pipelineStageLabel: candidate.pipeline_stage_label ?? undefined,
      pipelineStageSemantic: candidate.pipeline_stage_semantic ?? undefined,
      stagePosition: candidate.stage_position ?? undefined,
      scoringStatus: candidate.scoring_status ?? undefined,
    })) as Candidate[];
    
    return {
      data: candidates,
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Download candidate CV
export const downloadCandidateCV = async (
  accessToken: string,
  jobId: string,
  candidateId: string
): Promise<ApiResponse<Blob>> => {
  try {
    return callApi<Blob>(`${apiServerUrl}/api/jobs/offers/${jobId}/candidates/${candidateId}/cv`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      responseType: 'blob',
    });
  } catch (err) {
    console.error('Error in downloadCandidateCV service:', err);
    let errorMessage = 'Failed to download CV';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return {
      data: undefined,
      error: errorMessage
    };
  }
};

// Download job offer document (public, for candidates)
export const downloadJobOfferDocument = async (
  jobId: string
): Promise<ApiResponse<Blob>> => {
  try {
    return callApi<Blob>(`${apiServerUrl}/api/jobs/offers/${jobId}/job-offer/download`, {
      method: "GET",
      responseType: 'blob',
    });
  } catch (err) {
    console.error('Error in downloadJobOfferDocument service:', err);
    let errorMessage = 'Failed to download job offer document';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return {
      data: undefined,
      error: errorMessage
    };
  }
};

// Download job description document (private, for recruiters)
export const downloadJobDescriptionDocument = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<Blob>> => {
  try {
    return callApi<Blob>(`${apiServerUrl}/api/jobs/offers/${jobId}/documents/job_description/download`, {
      method: "GET",
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  } catch (err) {
    console.error('Error in downloadJobDescriptionDocument service:', err);
    let errorMessage = 'Failed to download job description document';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return {
      data: undefined,
      error: errorMessage
    };
  }
};

// Get a specific job offer by ID for public viewing (no auth required)
export const getPublicJobOfferById = async (
  jobId: string
): Promise<ApiResponse<JobOffer>> => {
  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/public`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    }
  });
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: response.error
    };
  }
  
  return {
    data: undefined,
    error: response.error
  };
};

// Upload a CV for a public job offer (no auth required)
export const uploadCvForPublicJob = async (
  jobId: string,
  file: File,
  source?: string | null
): Promise<ApiResponse<DocumentUploadResponse>> => {
  const formData = new FormData();
  formData.append('file', file);

  const query = source ? `?source=${encodeURIComponent(source)}` : '';
  return callApi<DocumentUploadResponse>(`${apiServerUrl}/api/jobs/offers/${jobId}/cv${query}`, {
    method: 'POST',
    body: formData,
  });
};

export interface CvSourceStat {
  source: string;
  count: number;
}

export interface SourcingTag {
  slug: string;
}

// Per-channel public CV upload counts for a job's Sourcing tab
export const getCvSourceStats = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<{ sources: CvSourceStat[] }>> => {
  return callApi<{ sources: CvSourceStat[] }>(
    `${apiServerUrl}/api/jobs/offers/${jobId}/cv-source-stats`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getSourcingTags = async (
  accessToken: string
): Promise<ApiResponse<{ items: SourcingTag[] }>> => {
  return callApi<{ items: SourcingTag[] }>(
    `${apiServerUrl}/api/jobs/sourcing-tags`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const createSourcingTag = async (
  accessToken: string,
  name: string
): Promise<ApiResponse<SourcingTag>> => {
  return callApi<SourcingTag>(`${apiServerUrl}/api/jobs/sourcing-tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ name }),
  });
};

export const deleteSourcingTag = async (
  accessToken: string,
  slug: string
): Promise<ApiResponse<void>> => {
  return callApi<void>(`${apiServerUrl}/api/jobs/sourcing-tags/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

// Get candidate assessment summary
export const getCandidateAssessmentSummary = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<CandidateAssessmentSummary>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<CandidateAssessmentSummary>(`${apiServerUrl}/api/candidates/${candidateId}/assessment`, config);
};

/** Admin-only: clear the suspicious-activity (cheating) flag on a single
 *  interview question after manual review. Returns the refreshed assessment
 *  summary so callers can propagate the removal across every badge surface. */
export const clearCandidateCheatingFlag = async (
  accessToken: string,
  candidateId: string,
  questionIndex: number
): Promise<ApiResponse<CandidateAssessmentSummary>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<CandidateAssessmentSummary>(
    `${apiServerUrl}/api/candidates/${candidateId}/assessment/cheating/${questionIndex}/clear`,
    config
  );
};

/** Queue scoring of a candidate's social resource (LeetCode / Scholar / blog).
 *  The actual result is delivered via SSE notification + persisted on the candidate. */
export const analyzeCandidateSocialResource = async (
  accessToken: string,
  candidateId: string,
  platform: SocialResourcePlatform
): Promise<ApiResponse<SocialResourceScoringTaskResponse>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await callApi<SocialResourceScoringTaskResponse>(
    `${apiServerUrl}/api/candidates/${candidateId}/social-resources/${platform}/analyze`,
    config
  );

  if (response.data?.task_id) {
    return { data: response.data, error: response.error };
  }

  return {
    data: undefined,
    error: response.error || `Failed to analyze ${platform} profile`,
  };
};

// Download candidate assessment PDF (generated by backend)
export const downloadCandidateAssessmentPdf = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<Blob>> => {
  try {
    return callApi<Blob>(`${apiServerUrl}/api/candidates/${candidateId}/assessment/pdf`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      responseType: 'blob',
    });
  } catch (err) {
    console.error('Error in downloadCandidateAssessmentPdf service:', err);
    let errorMessage = 'Failed to download assessment PDF';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return {
      data: undefined,
      error: errorMessage
    };
  }
};

// Accept a candidate.
// `payload` carries the booking-link decision (see utils/bookingLink): whether
// the acceptance email includes a link, and an optional per-send override of
// the recruiter's saved default.
export const acceptCandidate = async (
  accessToken: string,
  candidateId: string,
  payload?: AcceptCandidatePayload
): Promise<ApiResponse<CandidateStatusUpdate>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: payload ? JSON.stringify(payload) : undefined,
  };

  return await callApi<CandidateStatusUpdate>(`${apiServerUrl}/api/candidates/${candidateId}/accept`, config);
};

// Reject a candidate. `sendNotification: false` moves without the rejection email.
export const rejectCandidate = async (
  accessToken: string,
  candidateId: string,
  sendNotification = true
): Promise<ApiResponse<CandidateStatusUpdate>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ send_notification: sendNotification }),
  };

  return await callApi<CandidateStatusUpdate>(`${apiServerUrl}/api/candidates/${candidateId}/reject`, config);
};

// hire a candidate
export const hireCandidate = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<CandidateStatusUpdate>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<CandidateStatusUpdate>(`${apiServerUrl}/api/candidates/${candidateId}/hire`, config);
};

function mapPipelineStage(stage: {
  id: string;
  label: string;
  position: number;
  is_default: boolean;
  stage_type: string;
  automation_enabled?: boolean;
  has_automation_rule?: boolean;
  is_label_customized?: boolean;
}): PipelineStage {
  return {
    id: stage.id,
    label: stage.label,
    position: stage.position,
    isDefault: stage.is_default,
    stageType: stage.stage_type,
    // Default true matches backend column default for older payloads.
    automationEnabled: stage.automation_enabled ?? true,
    // Default false: missing field must not invent a configured automation.
    hasAutomationRule: stage.has_automation_rule ?? false,
    isLabelCustomized: stage.is_label_customized ?? false,
  };
}

/** GET /api/jobs/offers/{jobId}/pipeline — ordered stages for Kanban columns. */
export const getJobPipeline = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<JobPipeline>> => {
  const config: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  };

  const response = await callApi<PipelineApiResponse>(
    `${apiServerUrl}/api/jobs/offers/${jobId}/pipeline`,
    config
  );

  if (!response.data) {
    return { data: undefined, error: response.error };
  }

  const stages = [...response.data.stages]
    .sort((a, b) => a.position - b.position)
    .map(mapPipelineStage);

  return {
    data: {
      id: response.data.id,
      jobOfferId: response.data.job_offer_id,
      stages,
    },
    error: response.error,
  };
};

/** POST /api/candidates/{id}/pipeline-moves */
export const moveCandidatePipelineStage = async (
  accessToken: string,
  candidateId: string,
  body: {
    toStageId: string;
    insertAfterCandidateId?: string | null;
    withPosition?: boolean;
  }
): Promise<ApiResponse<MoveCandidateApiResponse>> => {
  const payload: Record<string, unknown> = {
    to_stage_id: body.toStageId,
  };
  if (body.withPosition) {
    payload.position = {
      insert_after_candidate_id: body.insertAfterCandidateId ?? null,
    };
  }

  const config: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  };

  return await callApi<MoveCandidateApiResponse>(
    `${apiServerUrl}/api/candidates/${candidateId}/pipeline-moves`,
    config
  );
};

/** PATCH /api/candidates/{id}/pipeline-position — same-stage reorder. */
export const updateCandidatePipelinePosition = async (
  accessToken: string,
  candidateId: string,
  insertAfterCandidateId: string | null
): Promise<ApiResponse<CandidatePositionApiResponse>> => {
  const config: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      insert_after_candidate_id: insertAfterCandidateId,
    }),
  };

  return await callApi<CandidatePositionApiResponse>(
    `${apiServerUrl}/api/candidates/${candidateId}/pipeline-position`,
    config
  );
};

/** POST /api/jobs/offers/{jobId}/pipeline/stages */
export const createPipelineStage = async (
  accessToken: string,
  jobId: string,
  body: { label: string; positionAfterStageId?: string | null }
): Promise<ApiResponse<PipelineStage>> => {
  const payload: Record<string, unknown> = { label: body.label };
  if (body.positionAfterStageId !== undefined) {
    payload.position_after_stage_id = body.positionAfterStageId;
  }

  const config: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  };

  const response = await callApi<{
    id: string;
    label: string;
    position: number;
    is_default: boolean;
    stage_type: string;
  }>(`${apiServerUrl}/api/jobs/offers/${jobId}/pipeline/stages`, config);

  if (!response.data) {
    return { data: undefined, error: response.error };
  }

  return {
    data: mapPipelineStage(response.data),
    error: response.error,
  };
};

/** PATCH /api/jobs/offers/{jobId}/pipeline/stages/{stageId} — rename, reorder, and/or toggle automation. */
export const updatePipelineStage = async (
  accessToken: string,
  jobId: string,
  stageId: string,
  body: { label?: string; position?: number; automationEnabled?: boolean }
): Promise<ApiResponse<PipelineStage>> => {
  const payload: Record<string, unknown> = {};
  if (body.label !== undefined) payload.label = body.label;
  if (body.position !== undefined) payload.position = body.position;
  if (body.automationEnabled !== undefined) {
    payload.automation_enabled = body.automationEnabled;
  }

  const config: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  };

  const response = await callApi<{
    id: string;
    label: string;
    position: number;
    is_default: boolean;
    stage_type: string;
    automation_enabled?: boolean;
    has_automation_rule?: boolean;
  }>(
    `${apiServerUrl}/api/jobs/offers/${jobId}/pipeline/stages/${stageId}`,
    config
  );

  if (!response.data) {
    return { data: undefined, error: response.error };
  }

  return {
    data: mapPipelineStage(response.data),
    error: response.error,
  };
};

/** DELETE /api/jobs/offers/{jobId}/pipeline/stages/{stageId} */
export const deletePipelineStage = async (
  accessToken: string,
  jobId: string,
  stageId: string
): Promise<ApiResponse<null>> => {
  const config: RequestInit = {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<null>(
    `${apiServerUrl}/api/jobs/offers/${jobId}/pipeline/stages/${stageId}`,
    config
  );
};

export const completeJobInformation = async (
  accessToken: string,
  jobId: string,
  formData: Record<string, unknown>
): Promise<ApiResponse<JobOffer>> => {
  const backendData = convertFormDataToBackendFormat(formData);
  
  const config = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(backendData),
  };

  const response = await callApi<ApiJobOffer>(`${apiServerUrl}/api/jobs/offers/${jobId}/complete-details`, config);
  
  if (response.data) {
    return {
      data: mapApiJobOfferToModel(response.data),
      error: undefined,
    };
  }
  
  // Return error response
  return {
    data: undefined,
    error: response.error,
  };
};

// Get planned questions for all interviews in a job
export const getJobPlannedQuestions = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<JobPlannedQuestionsListResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<JobPlannedQuestionsListResponse>(`${apiServerUrl}/api/jobs/offers/${jobId}/planned-questions`, config);
};

// Reload all interview assessments for a job
export const reloadAllJobInterviewAssessments = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<{
  message: string;
  job_id: string;
  reloaded_count: number;
  total_completed_interviews: number;
  status: string;
  failed_interviews?: Array<{
    interview_id: string;
    reason: string;
  }>;
}>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi(`${apiServerUrl}/api/interviews/job/${jobId}/reload_all_assessments`, config);
};

// Get candidate questions and answers
export const getCandidateQuestionsAnswers = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<CandidateQuestionsAnswersResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<CandidateQuestionsAnswersResponse>(`${apiServerUrl}/api/interviews/${candidateId}/questions_answers`, config);
};

// Delete a candidate
export const deleteCandidate = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<null>> => {
  const config: RequestInit = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await callApi<null>(`${apiServerUrl}/api/candidates/${candidateId}`, config);
};

// Download resume from resume bank
export const downloadResumeFromBank = async (
  accessToken: string,
  resumeId: string
): Promise<Blob> => {
  const url = `${apiServerUrl}/api/resume-bank/download/${resumeId}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to download resume");
  }

  return await response.blob();
};

// Update candidate email response type
interface UpdateEmailResponse {
  id: string;
  talent_id: string;
  talent_email: string;
  has_valid_email: boolean;
  message: string;
}

// Update candidate email
export const updateCandidateEmail = async (
  accessToken: string,
  candidateId: string,
  email: string
): Promise<ApiResponse<UpdateEmailResponse>> => {
  const config: RequestInit = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ email }),
  };

  const response = await callApi<UpdateEmailResponse>(
    `${apiServerUrl}/api/candidates/${candidateId}/email`,
    config
  );

  return {
    data: response.data,
    error: response.error,
  };
};
